import Chart from "chart.js";
import { barTapped } from "./app/bar-tapped";
import { renderComplete } from "./app/render-complete";
import { getDataLabelsConfiguration, getDefaultDataLabelsConfiguration } from "./chart-datalabels";
import { customTooltips, tooltipConfiguration } from "./chart-tooltips";
import { colors } from "./colors";
import { environments } from "./environments";
import { views } from "./helpers/views";
import { HomeDateSupport } from "./home-date-support";

/**
 * @typedef {Object} HWGraphContext
 * @property {string} view The view, e.g. `year`, `month`, `week`, `day`, `hour`.
 * @property {string} lang The language, e.g. `nl`, `fr`, `en`.
 * @property {boolean} fullscreen Whether the graph is in fullscreen mode.
 * @property {string} type The type of data, e.g. `power`, `solar`, `gas`, `water`.
 * @property {boolean} three_phases Indicates whether we should show three phases.
 * @property {HomeDateSupport} dateSupport An instance of `HomeDateSupport` configured for the home's timezone.
 *
 * @typedef {import("chart.js").ChartData & { hw: HWGraphContext }} GraphData
 */

const getChartConfiguration = (date, view, lang, type, fullscreen, debug = false, environment = "apps", three_phases = false, timezone) => {
    console.debug(`getChartConfiguration(view=${view}, lang=${lang}, type=${type}, fullscreen=${fullscreen}), debug = ${debug}, environment = ${environment}`)

    const isSolarGraph = type === 'solar' || type === 'solar_combined'

    const dateSupport = new HomeDateSupport(timezone)
    return {
        type: views[view] <= views.day ? 'line' : 'bar',

        /** @type {GraphData} */
        data: {
            datasets: [
                {
                    fill: 'origin',
                    pointRadius: debug ? 3 : 0,
                    borderWidth: views[view] <= views.day ? 2 : 1,
                    borderSkipped: false,
                    barThickness: views[view] == views.year ? environments(environment).barThickness : 10,
                    minBarLength: 1,
                    data: [],
                    datalabels: getDefaultDataLabelsConfiguration(0),
                    ...(debug && {pointBorderColor: 'yellow'}),
                    ...(debug && {pointBackgroundColor: 'yellow'}),
                },
                {
                    fill: 'origin',
                    pointRadius: debug ? 3 : 0,
                    borderWidth: three_phases && views[view] <= views.day ? 2 : 1,
                    borderSkipped: false,
                    barThickness: views[view] == views.year ? environments(environment).barThickness : 10,
                    minBarLength: 1,
                    data: [],
                    datalabels: getDefaultDataLabelsConfiguration(1),
                    ...(debug && {pointBorderColor: 'yellow'}),
                    ...(debug && {pointBackgroundColor: 'yellow'}),
                },
                {
                    fill: 'origin',
                    pointRadius: debug ? 3 : 0,
                    borderWidth: views[view] <= views.day ? 2 : 1,
                    borderSkipped: false,
                    barThickness: views[view] == views.year ? environments(environment).barThickness : 10,
                    minBarLength: 1,
                    data: [],
                    datalabels: getDefaultDataLabelsConfiguration(2),
                    ...(debug && {pointBorderColor: 'yellow'}),
                    ...(debug && {pointBackgroundColor: 'yellow'}),
                },
            ],
            labels: (() => {
                if (view === 'hour') {
                    const d = dateSupport.parseDateString(date, 'YYYY/MM/DD/HH')

                    return [
                        d.minutes(0).toDate(),
                        d.minutes(10).toDate(),
                        d.minutes(20).toDate(),
                        d.minutes(30).toDate(),
                        d.minutes(40).toDate(),
                        d.minutes(50).toDate(),
                        d.minutes(60).toDate()
                    ]
                }

                if (view === 'day') {
                    const d = dateSupport.parseDateString(date, 'YYYY/MM/DD')

                    return [
                        d.hours(2).toDate(),
                        d.hours(7).toDate(),
                        d.hours(12).toDate(),
                        d.hours(17).toDate(),
                        d.hours(22).toDate(),
                    ];
                }
            })(),
            hw: {view, lang, fullscreen, type, three_phases, dateSupport},
        },
        options: {
            animation: {
                duration: environment === 'apps' ? 400 : 0, // enable animations for "apps" environment
                easing: 'easeOutQuart'
            },
            events: view === 'now' ?
                [] : // disable all interaction on now (live) view
                ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
            responsive: true,
            maintainAspectRatio: false,
            onClick: (event, activeElements) => {
                if (!activeElements.length > 0) {
                    console.log('return onclick')
                    return
                }

                const activeElement = activeElements[0]
                const timestamp = activeElement._xScale._timestamps.data[activeElement._index]
                const view = activeElement._xScale._unit

                const date = dateSupport.formatTsdbPeriod(timestamp, view)
                if (!date) {
                    return
                }

                barTapped({
                    date: date,
                    view: view
                })
            },
            tooltips: {...tooltipConfiguration, custom: fullscreen && view !== 'now' ? customTooltips : null},
            plugins: {
                datalabels: getDataLabelsConfiguration(view, fullscreen),
            },
            layout: {
                padding: {
                    left: 5,
                    right: 5,
                    top: fullscreen ? 42 : 15,
                    bottom: 5
                }
            },
            legend: {
                display: false
            },
            scales: {
                xAxes: [
                    {
                        display: environments(environment).xAxis.display,
                        type: 'time',
                        time: {
                            tooltipFormat: 'x', // milliseconds
                            isoWeekday: true,
                            displayFormats: {
                                second: 'HH:mm:ss',
                                minute: 'HH:mm',
                                hour: 'HH:mm',
                                day: 'dd',
                                week: 'W',
                                month: 'MMM'
                            },
                            unit: ({
                                now: 'second',
                                hour: 'minute',
                                day: 'hour',
                                week: 'day',
                                month: 'week',
                                year: 'month',
                            }[view])
                        },
                        offset: views[view] > views.day,
                        gridLines: {
                            display: debug,
                            drawTicks: environments(environment).xAxis.drawTicks,
                            color: debug ? 'yellow' : colors.gridLineColor,
                        },
                        // @url https://www.chartjs.org/docs/latest/axes/cartesian/time.html
                        ticks: {
                            source: views[view] <= views.day ? 'labels' : 'data',
                            bounds: 'data', // "data" or "ticks"
                            // distribution: 'series', // "linear" or "series"
                            padding: 0,
                            maxRotation: 0,
                            callback: function (_value, index, values) {
                                return dateSupport.formatGraphTick(values[index].value, view)
                            },
                        }
                    }
                ],
                yAxes: [
                    {
                        display: environments(environment).yAxis.display,
                        position: 'right',
                        stacked: false,
                        weight: 1,
                        gridLines: {
                            display: environments(environment).yAxis.gridLines,
                            drawBorder: false,
                            drawTicks: false,
                            color: debug ? 'yellow' : environments(environment).colors.gridLineColor,
                            zeroLineColor: colors.gridLineColor
                        },
                        ticks: {
                            beginAtZero: true,
                            autoskip: false,
                            labelOffset: -3,
                            padding: 5,
                            maxTicksLimit: environment === 'apps'
                                ? Math.max(3, Math.round((window.outerHeight / 50)+1.6)) // responsive/dynamic
                                : environments(environment).yAxis.maxTicksLimit,   // (IOS) Widgets
                            reverse: views[view] <= views.day && isSolarGraph
                        }
                    }
                ]
            }
        },
        plugins: [
            {
                beforeUpdate: (chart, _options) => {

                    // Calculate range of first yAxis

                    let {view, fullscreen} = chart.data.hw

                    // Ignore empty datasets and the charge percentage dataset that is specifically supplied in battery graphs
                    // The charge percentage dataset is mapped on a different y-axis.
                    let datasets = chart.data.datasets.filter((dataset) => dataset.data.length !== 0 && dataset.label !== 'charge')

                    // Set the min and max values in each remaining dataset
                    datasets.forEach((dataset) => {
                        // We do < or > checks as NaN values can exist in the dataset,
                        // ensuring that the min and max values are always numbers.
                        // The Math functions would resolve to NaN unless we
                        // explicitly resolve it ourselves.
                        dataset.min = dataset.data.reduce((min, p) => p.y < min ? p.y : min, 0)
                        dataset.max = dataset.data.reduce((max, p) => p.y > max ? p.y : max, 0)
                    })

                    // Calculate the min and max value across all datasets
                    const minValue = datasets.reduce((total, dataset) => Math.min(dataset.min, total), 0)
                    const maxValue = datasets.reduce((total, dataset) => Math.max(dataset.max, total), 0)

                    const yAxisTicks = chart.options.scales.yAxes[0].ticks
                    applyYAxisThresholds(yAxisTicks, minValue, maxValue, type, view, fullscreen, isSolarGraph)
                },
                afterLayout: (chart, options) => {
                    // Code that tries determine and set the background gradients of Energy graphs
                    // @todo this, along many other things in "html-graph-renderer", should be refactored into something
                    //    more readable, cause right now it really sucks :/
                    // Calculate the right gradient for graphs based on rendered data
                    let yScale = chart.scales['y-axis-0']
                    let bgGradient, borderGradient

                    if (three_phases && views[view] <= views.day) {
                        chart.data.datasets.forEach((dataset, index) => {
                            if (dataset.data.length === 0) {
                                return
                            }

                            let gradient = chart.ctx.createLinearGradient(0, 0, 0, chart.height)
                            let {min, max} = dataset

                            const getColorStop = (val) =>
                                Math.max(0, Math.min(yScale.getPixelForValue(val || 0) / chart.height, 1))

                            if (min < 0 && max > 0) {
                                // a dataset with positive and negative values should use a double gradient
                                gradient.addColorStop(getColorStop(max), colors[`power_import_l${index+1}`].gradient_start) // import
                                gradient.addColorStop(getColorStop(0), colors[`power_import_l${index+1}`].gradient_end) // import
                                gradient.addColorStop(getColorStop(0), colors[`power_export_l${index+1}`].gradient_end) // export
                                gradient.addColorStop(getColorStop(min), colors[`power_export_l${index+1}`].gradient_start) // export

                                let bg = chart.ctx.createLinearGradient(0, 0, 0, chart.height)
                                bg.addColorStop(getColorStop(0), colors[`power_import_l${index+1}`].border) // import
                                bg.addColorStop(getColorStop(0), colors[`power_export_l${index+1}`].border) // export
                                dataset.borderColor = bg
                            } else {
                                gradient.addColorStop(
                                    getColorStop(min),
                                    min < 0 ? colors[`power_export_l${index+1}`].gradient_start : colors[`power_import_l${index+1}`].gradient_end
                                )

                                gradient.addColorStop(getColorStop(max),
                                    min < 0 ? colors[`power_export_l${index+1}`].gradient_end : colors[`power_import_l${index+1}`].gradient_start
                                )

                                dataset.borderColor = min < 0 || isSolarGraph ? colors[`power_export_l${index+1}`].border : colors[`power_import_l${index+1}`].border
                            }

                            dataset.backgroundColor = gradient
                        })

                        return
                    }

                    switch (type) {
                        case 'gas':
                            borderGradient = [[colors.gas_import.border, colors.gas_import.border]]
                            bgGradient = [[colors.gas_import.gradient_end, colors.gas_import.gradient_start]]
                            break

                        case 'water':
                            borderGradient = [[colors.water_import.border, colors.water_import.border]]
                            bgGradient = [[colors.water_import.gradient_end, colors.water_import.gradient_start]]
                            break

                        default:
                            // power
                            bgGradient = [
                                [
                                    colors.power_import.gradient_end,
                                    colors.power_import.gradient_start,
                                    colors.power_export.gradient_end,
                                    colors.power_export.gradient_start,
                                ], [
                                    view === 'day' ? colors.power_export.gradient_start : colors.power_export.gradient_end,
                                    view === 'day' ? colors.power_export.gradient_end : colors.power_export.gradient_start
                                ]
                            ]

                            borderGradient = [
                                [
                                    isSolarGraph ? colors.power_export.border : colors.power_import.border,
                                    colors.power_export.border
                                ],
                                [
                                    colors.power_export.border,
                                    isSolarGraph ? colors.power_export.border : colors.power_import.border,
                                ]
                            ]
                    }

                    chart.data.datasets.forEach((dataset, index) => {
                        if (dataset.data.length === 0) {
                            return
                        }

                        let gradient = chart.ctx.createLinearGradient(0, 0, 0, chart.height)
                        let {min, max} = dataset

                        const getColorStop = (val) =>
                            Math.max(0, Math.min(yScale.getPixelForValue(val || 0) / chart.height, 1))

                        if (min < 0 && max > 0) {
                            // a dataset with positive and negative values should use a double gradient
                            gradient.addColorStop(getColorStop(max), bgGradient[index][1]) // import
                            gradient.addColorStop(getColorStop(0), bgGradient[index][0]) // import
                            gradient.addColorStop(getColorStop(0), bgGradient[index][2]) // export
                            gradient.addColorStop(getColorStop(min), bgGradient[index][3]) // export

                            let bg = chart.ctx.createLinearGradient(0, 0, 0, chart.height)
                            bg.addColorStop(getColorStop(0), borderGradient[index][0]) // import
                            bg.addColorStop(getColorStop(0), borderGradient[index][1]) // export
                            dataset.borderColor = bg
                        } else {
                            gradient.addColorStop(getColorStop(min), bgGradient[index][min < 0 ? 3 : 0])
                            gradient.addColorStop(getColorStop(max), bgGradient[index][min < 0 ? 2 : 1])
                            dataset.borderColor = borderGradient[index][min < 0 ? 1 : 0]
                        }

                        dataset.backgroundColor = gradient
                    })
                },
                beforeDraw: (chart) => {
                    // Return in case the rendering has not yet complete (!chart.data.datasets.length) or
                    // when our total dataset.data length === 0, e.g. we DO have data
                    if (!chart.data.datasets.length || chart.data?.datasets?.reduce((length, dataset) => length + dataset.data.length, 0)) {
                        chart.ctx.globalCompositeOperation = 'source-over' // Restore default global composite operation

                        return
                    }

                    chart.ctx.globalCompositeOperation = 'destination-over' // Make sure text is on top of the grid lines
                    chart.ctx.font = `bold 15px ${Chart.defaults.global.defaultFontFamily}`
                    chart.ctx.fillStyle = colors.datalabels
                    chart.ctx.textAlign = 'center'
                    chart.ctx.fillText("No data", chart.width / 2, chart.height / 2)
                },
                afterRender: (chart, options) => {
                    if (chart.data.datasets.length) {
                        renderComplete()
                    }
                },
            }
        ]
    }
}

/**
 * Apply min and max values to the given y-axis.
 *
 * The view determines if a threshold should be taken into account
 * and the type determines the threshold value.
 *
 * @param {Object} yAxisTicks The y-axis ticks configuration object.
 * @param {number} minValue The minimum value of the data.
 * @param {number} maxValue The maximum value of the data.
 * @param {string} type The type of data, e.g. `power`, `solar`, `gas`, `water`.
 * @param {string} view The view, e.g. `year`, `month`, `week`, `day`, `hour`.
 * @param {boolean} fullscreen Whether the graph is in fullscreen mode.
 * @param {boolean} isSolarGraph Whether the graph is a solar graph.
 */
function applyYAxisThresholds(yAxisTicks, minValue, maxValue, type, view, fullscreen, isSolarGraph) {
    let yAxisMin = minValue
    let yAxisMax = maxValue

    yAxisTicks.suggestedMin = 0

    if (view === 'now' || view === 'day') {
        const NEGATIVE_THRESHOLD = getGraphYAxisThreshold(type, true)
        const POSITIVE_THRESHOLD = getGraphYAxisThreshold(type)
        const MODIFIER_FACTOR = 1.3;

        // Check which type of values we have
        const haveNegativeValues = yAxisMin < 0
        const havePositiveValues = yAxisMax > 0

        if (haveNegativeValues && havePositiveValues) {
            // Here we make a window of the threshold value so that the y-axis
            // snugly surrounds the visible values and maintains a consistent minimum difference.

            /**
             * To illustrate with an example:
             *
             * If the min value is -10 and the max value is 10, the difference between the two is 20
             * and with a threshold of 100 the difference is then 80.
             * That 80 then needs to be distributed evenly on both sides of the min and max values.
             * This will result in a range of -50 to 50.
             */

            const differenceBetweenBothEnds = yAxisMax - yAxisMin

            // If the difference between the min and max is less than the threshold, adjust the min and max
            if (differenceBetweenBothEnds < POSITIVE_THRESHOLD) {
                const remainderFromMax = POSITIVE_THRESHOLD - differenceBetweenBothEnds
                yAxisMin = yAxisMin - remainderFromMax / 2
                yAxisMax = yAxisMax + remainderFromMax / 2
            } else {
                yAxisMin = Math.min(yAxisMin, NEGATIVE_THRESHOLD)
                yAxisMax = Math.max(yAxisMax, POSITIVE_THRESHOLD)
            }
        } else {
            // Calculate the min and max value for the y-axis, constrained by the threshold values
            // Only one side will be non zero in this case.
            yAxisMin = haveNegativeValues ? Math.min(yAxisMin, NEGATIVE_THRESHOLD) : 0
            yAxisMax = havePositiveValues ? Math.max(yAxisMax, POSITIVE_THRESHOLD) : 0
        }

        const modifierMin = yAxisMin < NEGATIVE_THRESHOLD && view == 'now' ? MODIFIER_FACTOR : 1
        const modifierMax = yAxisMax > POSITIVE_THRESHOLD && view == 'now' ? MODIFIER_FACTOR : 1

        yAxisTicks.suggestedMin = modifierMin * yAxisMin
        yAxisTicks.suggestedMax = modifierMax * yAxisMax
    } else {
        // Make room for datalabels on top of bar charts by extending the yAxis a bit
        // The added space ensures that the datalabels do not interfere with values atop the bars.
        yAxisTicks.suggestedMax = fullscreen && views[view] >= views.week ? 1.05 * yAxisMax : 0
    }

    if (isSolarGraph && yAxisMin >= 0 && views[view] <= views.day) {
        /**
         * Make sure that "zero lines" (all datavalues are zero) are at the bottom of the graph
         * in line graphs by setting the suggested minimal value to -1
         */
        yAxisTicks.suggestedMin = -1
    }
}

/**
 * Returns the threshold value for the main y-axis (right side) of a graph.
 *
 * This threshold is used to ensure that the y-axis does not go below this value
 * so that it does not adjusts the scale constantly when the data is close to zero.
 *
 * @param {string} graphType The type of graph, e.g. `power`, `solar`, `main_connection`, `battery`.
 * @param {boolean} negative Set to true if the threshold should be negative.
 */
function getGraphYAxisThreshold(graphType, negative = false) {
    let value = 20

    if (graphType === 'main_connection') {
        value = 100
    } else if (graphType === 'gas') {
        value = 0.1
    }

    return negative ? -value : value
}

export { applyYAxisThresholds, getChartConfiguration };

